import { Avatar, Box, TextField, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { pipelineErrorAtom, pipelineStatusAtom } from './atoms'

import { ConfirmWithDialogButton } from 'shared/ui'
import { removePipeline } from './utils'
import { useAtom } from 'infrastructure/state/jotai.wrapper'
import { usePipelineStatusPolling } from './hooks'

interface PipelineStatusProps {
  treeId: string
  versionId: string
}

export function PipelineStatus({ treeId, versionId }: PipelineStatusProps) {
  const [pipelineStatus, setPipelineStatus] = useAtom(pipelineStatusAtom)
  const [error, setError] = useAtom(pipelineErrorAtom)

  // Get the stable callback for checking pipeline status
  const checkStatus = usePipelineStatusPolling()

  // Start polling when treeId or versionId changes
  useEffect(() => {
    const cleanup = checkStatus(treeId, versionId)
    return cleanup
  }, [treeId, versionId, checkStatus])

  const handleRemovePipeline = async () => {
    try {
      await removePipeline(treeId, versionId)
      setPipelineStatus('Successfully removed pipeline.')
      setError(null) // Clear any pipeline errors when successfully removing pipeline
    } catch (error) {
      setPipelineStatus(
        `Error removing pipeline: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Avatar
          sx={{
            width: 12,
            height: 12,
            bgcolor: error ? 'warning.main' : 'success.main',
          }}
        />
        <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
          Pipeline
        </Typography>
      </Box>

      {error && (
        <Typography variant="body2" color="error" gutterBottom sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <TextField
          value={pipelineStatus}
          placeholder="Pipeline status will appear here..."
          multiline
          rows={2}
          variant="outlined"
          size="small"
          label="Status"
          sx={{ flexGrow: 1 }}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={2}>
        <ConfirmWithDialogButton
          variant="outlined"
          color="error"
          dialogTitle="Remove Pipeline"
          dialogContent={
            <Typography>
              Are you sure you want to remove the SVG to LaTeX conversion jobs?
              All processing results will be lost.
            </Typography>
          }
          onClick={handleRemovePipeline}
          cta="Remove"
          cancel="Cancel"
        >
          Remove Pipeline
        </ConfirmWithDialogButton>
      </Box>
    </Box>
  )
}
